import { ref } from 'vue';
import { defineStore } from 'pinia';
import { taggedLogger } from '@/util/taggedLogger';
import {
    AmplitudeFeature,
    allAmplitudeFeaturesOff,
    isAmplitudeVariantEnabled,
} from '@/domain/featureToggles/amplitudeFeatureToggle';
import { resolveAmplitudeExperimentPort } from '@/stores/featureToggles/amplitudeExperimentClient';
import type { AmplitudeFeatureSet } from '@/stores/featureToggles/amplitudeFeatures.types';

const log = taggedLogger('amplitude-features');

/**
 * Model store holding the Amplitude feature-toggle state for the session.
 *
 * The toggles are asynchronous to populate but synchronous to read, so variants are resolved
 * once per `refresh()` and snapshotted into reactive state — never read per render, which would
 * emit an exposure event on every evaluation.
 *
 * `flags` is deliberately writable state (a documented deviation from "state is only mutated
 * inside actions"), mirroring `useOrganisationProfile().features` as the seam tests drive the
 * gate through. `refresh()` is the only production writer.
 */
export const useAmplitudeFeatures = defineStore('amplitude-features', () => {
    const flags = ref<AmplitudeFeatureSet>(allAmplitudeFeaturesOff());

    /** Whether `start()` has already resolved. A second `start()` is a silent SDK no-op. */
    let hasStarted = false;

    // SERIALISES, it does not coalesce. Each caller gets its own resolveFlags() run, queued
    // behind the previous one. Coalescing would be a correctness bug: the post-identify
    // fetch() would be swallowed by the bootstrap start() still in flight, leaving
    // cohort-targeted flags resolved against the anonymous device id for the whole session.
    let queue: Promise<void> = Promise.resolve();

    function isEnabled(feature: AmplitudeFeature): boolean {
        return flags.value[feature];
    }

    async function resolveFlags(): Promise<void> {
        const client = resolveAmplitudeExperimentPort();
        if (client === undefined) {
            // Deliberate no-op, not a fail-closed reset. This branch is reachable ONLY before
            // the SDK has finished initialising (it yields no client while its config is
            // unset), so `flags` is necessarily still all-false here and a reset would change
            // nothing in production. It would, however, clobber state written directly through
            // the `flags` seam. Do not "tidy" this into a reset.
            log.debug('Experiment client unavailable; feature toggles left untouched');
            return;
        }
        try {
            if (hasStarted) {
                await client.fetch();
            } else {
                await client.start();
                // Only after start() RESOLVES, so a rejected start() retries as start().
                hasStarted = true;
            }
            const resolved = allAmplitudeFeaturesOff();
            for (const feature of Object.values(AmplitudeFeature)) {
                resolved[feature] = isAmplitudeVariantEnabled(client.variant(feature).value);
            }
            // Atomic commit — a throw part-way through the loop can never publish a half-set.
            flags.value = resolved;
        } catch (error) {
            log.error('Failed to resolve Amplitude feature toggles; leaving them off', error);
            flags.value = allAmplitudeFeaturesOff();
        }
    }

    /** Resolve every declared feature. Never rejects and never throws. */
    async function refresh(): Promise<void> {
        const next = queue.then(resolveFlags);
        queue = next;
        return next;
    }

    return { flags, isEnabled, refresh };
});
